import React, { useState } from "react";
import { getS3 } from "../../utils/Download";
import DOMPurify from 'dompurify';
// SQ 3.34 - 3.60 : Handles the certification details when user clicks on request
export const CertificationDetails = ({
    onClose,
    showDetails,
    setShowDetails,
    certificateDetails,
    onUploadCertificate,
    onPostApproval,
    onApprovalSubmit
}) => {

    const getFieldValue = (field, defaultValue = '') => {
        if (!field) return defaultValue;
        if (field.Valid !== undefined && !field.Valid) return defaultValue;
        if (field.Bool !== undefined) return field.Bool; // For boolean fields
        return field.String !== undefined ? field.String : defaultValue;
    };

    const [status, setStatus] = useState(getFieldValue(certificateDetails.pass_reattempt_status, "Passed"));
    const [certificateFile, setCertificateFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [uploadedCertificateUrl, setUploadedCertificateUrl] = useState(
        getFieldValue(certificateDetails.certificate_url.String)
    );

    const certificateUrl = getFieldValue(certificateDetails.certificate_url);
    const isViewOnly = getFieldValue(certificateDetails.pass_reattempt_status) !== "" && certificateUrl;
    const [validationError, setValidationError] = useState(null);
    const isRejected = getFieldValue(certificateDetails.certification_completion_approval, "Approved") === "Rejected";
    const [isReupload, setIsReupload] = useState(null); // Initialize as null

    // Shared handler for file selection (used by both initial upload and re-upload)
    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.type !== 'application/pdf') {
                setValidationError("Only PDF files are allowed.");
                setCertificateFile(null);
                setIsReupload(null);
            } else if (file.size > 10 * 1024 * 1024) {
                setValidationError("File size must be less than 10 MB.");
                setCertificateFile(null);
                setIsReupload(null);
            } else {
                setValidationError(null);
                setCertificateFile(file);
                if (isRejected) {
                    setIsReupload(true); // Set for re-upload
                    setUploadedCertificateUrl(null); // Clear old URL for re-upload
                } else {
                    setIsReupload(false); // Set for initial upload
                }
            }
        }
    };

   // seq 3.22 - payload along with reupload status
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setIsSubmitting(true);

        try {
            const uploadPromise = certificateFile
                ? onUploadCertificate({
                    request_id: certificateDetails.request_id,
                    certificate_file: certificateFile,
                    emp_id: certificateDetails.emp_id,
                    certificate_name: certificateDetails.certificate_name,
                    provider: certificateDetails.provider,
                    specialization: certificateDetails.provider,
                    year_of_completion: new Date().toISOString().slice(0, 19).replace('T', ' '),
                    original_filename: certificateFile?.name,
                    is_reupload: isReupload
                })
                : Promise.resolve({ url: certificateUrl });

            const approvalPromise = onPostApproval({
                request_id: certificateDetails.request_id,
                emp_id: certificateDetails.emp_id,
                pass_reattempt_status: status,
                certificate_url: certificateFile ? undefined : certificateUrl, // Will be updated after upload if needed
                certificate_id: certificateDetails.certificate_id,
                client_dependency: certificateDetails.client_dependency?.Bool || false,
                domain: certificateDetails.domain
            });

            const [uploadResponse, approvalResponse] = await Promise.all([uploadPromise, approvalPromise]);
            if (uploadResponse.url) {
                setUploadedCertificateUrl(uploadResponse.url);
            }
            const isUploadSuccessful = certificateFile ? uploadResponse.data.status === 200 : true;
            const isApprovalSuccessful = approvalResponse.data.status === 200;
            if (isUploadSuccessful && isApprovalSuccessful) {
                if (certificateFile) {
                    await onPostApproval({
                        request_id: certificateDetails.request_id,
                        emp_id: certificateDetails.emp_id,
                        pass_reattempt_status: status,
                        certificate_url: uploadResponse.url,
                        certificate_id: certificateDetails.certificate_id,
                        client_dependency: certificateDetails.client_dependency?.Bool || false,
                        domain: certificateDetails.domain
                    });
                }
                onClose();
            } else {
                throw new Error("Submission failed");
            }

        } catch (err) {
            console.error("Submission error:", err);
            setError(err.message || "Failed to submit approval");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        // Reset form state
        setStatus(getFieldValue(certificateDetails.pass_reattempt_status, "Passed"));
        setCertificateFile(null);
        setValidationError(null);
        setIsReupload(null); // Reset isReupload
        const fileInput = document.getElementById('certificateUpload');
        if (fileInput) fileInput.value = '';
        onClose();
    };
    return (
        <div
            className={`modal fade ${showDetails ? "show d-block" : ""}`}
            style={{
                display: 'block',
                backgroundColor: 'rgba(0,0,0,0.5)',
                backdropFilter: 'blur(2px)'
            }}
        >
            <div className="modal-dialog certification-modal approval-modal-width">
                <div className="modal-content">
                    <div className="modal-header">
                        <h6 className="modal-title">
                            {isViewOnly ?
                                `Certification Details - ${certificateDetails.emp_name}`
                                :
                                `Certification Approval - ${certificateDetails.req_id}`
                            }
                        </h6>
                        <button
                            type="button"
                            className="btn-close font-12"
                            onClick={handleClose}
                            aria-label="Close"
                        />
                    </div>

                    <div className="modal-body">
                        <div className="row">
                            {/* Certification Name */}
                            <div className="col-md-8 mb-3">
                                <label className="font-12 mb-1 regular-text grey-text d-block">
                                    Certification Name
                                </label>
                                <div className="d-flex justify-content-start align-items-center">
                                    <p className="font-12 semibold-text">
                                        {certificateDetails.certificate_name}
                                    </p>
                                    {certificateDetails.client_dependency?.Bool && (
                                        <span className="client-badge ms-2">Client</span>
                                    )}
                                </div>
                            </div>

                            {/* Provider */}
                            <div className="col-md-4 mb-3">
                                <label className="font-12 mb-1 regular-text grey-text d-block">
                                    Provider
                                </label>
                                <p className="font-12 semibold-text">
                                    {certificateDetails.provider}
                                </p>
                            </div>

                            {/* Status - Different display based on view mode */}
                            <div className="col-md-8 mb-3">
                                <label className="font-12 mb-1 regular-text grey-text d-block">
                                    Status {!isViewOnly && <span className="text-danger">*</span>}
                                </label>

                                {isViewOnly ? (
                                    <p className="font-12 semibold-text">
                                        {getFieldValue(certificateDetails.pass_reattempt_status, "Pending")}
                                    </p>
                                ) : (
                                    <div className="mt-1 d-flex gap-3">
                                        {certificateDetails.client_dependency?.Bool ? (
                                            <>
                                                <div className="form-check form-check-inline d-flex align-items-center mb-0 gap-2">
                                                    <input
                                                        className="form-check-input custom-radio-btn mt-0"
                                                        type="radio"
                                                        name="status"
                                                        id="statusPassed"
                                                        value="Passed"
                                                        checked={status === "Passed"}
                                                        onChange={() => setStatus("Passed")}
                                                        disabled={isSubmitting}
                                                    />
                                                    <label
                                                        className="form-check-label font-12 medium-text"
                                                        htmlFor="statusPassed"
                                                    >
                                                        Passed
                                                    </label>
                                                </div>
                                                <div className="form-check form-check-inline d-flex align-items-center mb-0 gap-2">
                                                    <input
                                                        className="form-check-input custom-radio-btn mt-0"
                                                        type="radio"
                                                        name="status"
                                                        id="statusReattempt"
                                                        value="Reattempt"
                                                        checked={status === "Reattempt"}
                                                        onChange={() => setStatus("Reattempt")}
                                                        disabled={isSubmitting}
                                                    />
                                                    <label
                                                        className="form-check-label font-12 medium-text"
                                                        htmlFor="statusReattempt"
                                                    >
                                                        Reattempt
                                                    </label>
                                                </div>
                                            </>
                                        ) : (
                                            // Show Passed and Failed if client_dependency is false
                                            <>
                                                <div className="form-check form-check-inline d-flex align-items-center mb-0 gap-2">
                                                    <input
                                                        className="form-check-input custom-radio-btn mt-0"
                                                        type="radio"
                                                        name="status"
                                                        id="statusPassed"
                                                        value="Passed"
                                                        checked={status === "Passed"}
                                                        onChange={() => setStatus("Passed")}
                                                        disabled={isSubmitting}
                                                    />
                                                    <label
                                                        className="form-check-label font-12 medium-text"
                                                        htmlFor="statusPassed"
                                                    >
                                                        Passed
                                                    </label>
                                                </div>
                                                <div className="form-check form-check-inline d-flex align-items-center mb-0 gap-2">
                                                    <input
                                                        className="form-check-input custom-radio-btn mt-0"
                                                        type="radio"
                                                        name="status"
                                                        id="statusFailed"
                                                        value="Failed"
                                                        checked={status === "Failed"}
                                                        onChange={() => setStatus("Failed")}
                                                        disabled={isSubmitting}
                                                    />
                                                    <label
                                                        className="form-check-label font-12 medium-text"
                                                        htmlFor="statusFailed"
                                                    >
                                                        Failed
                                                    </label>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Certificate - Different display based on view mode */}
                            <div className="col-md-4 mb-3">
                                <label className="font-12 mb-1 regular-text grey-text d-block">
                                    {status === "Failed" ? "Attachments" : "Certificate"}{!isViewOnly && <span className="text-danger">*</span>}
                                </label>
                                <input
                                    type="file"
                                    id="certificateUpload"
                                    style={{ display: "none" }}
                                    onChange={handleFileSelect}
                                    accept=".pdf"
                                    disabled={isSubmitting}
                                />
                                {isViewOnly && !certificateFile ? (
                                    certificateUrl && certificateUrl !== "null" ? (
                                        <>
                                            <a
                                                style={{
                                                    cursor: "pointer",
                                                    whiteSpace: "normal",
                                                    overflowWrap: "break-word",
                                                    maxWidth: "200px",
                                                    display: "inline-flex" ,
                                                    alignItems: "center"
                                                }}
                                                rel="noopener noreferrer"
                                                className="d-flex align-items-center bg-white p-2 border border-light-subtle rounded-3 shadow-sm text-decoration-none font-10 semibold-text text-dark "
                                                onClick={async (e) => {
                                                    e.preventDefault();
                                                    window.open(await getS3(certificateUrl), "_blank");
                                                }}
                                                target="_blank"
                                            >
                                                <img src="/Images/pdf-icon.svg" alt="pdf-icon" className="me-1" />
                                                {certificateUrl.split('/').pop()}
                                            </a>
                                            {isRejected && (
                                                <label
                                                    htmlFor="certificateUpload"
                                                    className="btn upload-btn mt-1 font-10 semibold-text mt-2"
                                                >
                                                    <img src="/Images/upload-icon.svg" alt="re-upload-icon" className="me-1" />
                                                    Re-upload
                                                </label>
                                            )}
                                        </>
                                    ) : (
                                        <p className="font-12 semibold-text grey-text">Not available</p>
                                    )
                                ) : (
                                    <>
                                        {certificateFile ? (
                                            <div className="d-flex align-items-center bg-white p-2 border border-light-subtle rounded-3 shadow-sm">
                                                <img
                                                    src="/Images/pdf-icon.svg"
                                                    alt="file-icon"
                                                    className="me-1"
                                                />
                                                <span className="font-10 semibold-text text-dark">
                                                    {certificateFile.name}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="btn-close ms-auto font-10"
                                                    aria-label="Remove"
                                                    onClick={() => {
                                                        setCertificateFile(null);
                                                        setIsReupload(null);
                                                        setValidationError(null);
                                                        setUploadedCertificateUrl(getFieldValue(certificateDetails.certificate_url)); // Reset to original URL
                                                        document.getElementById('certificateUpload').value = '';
                                                    }}
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                        ) : isReupload === true ? (
                                            <label
                                                htmlFor="certificateUpload"
                                                className="btn upload-btn mt-1 font-10 semibold-text"
                                                style={{ cursor: isSubmitting ? "not-allowed" : "pointer" }}
                                            >
                                                <img
                                                    src="/Images/upload-icon.svg"
                                                    alt="re-upload-icon"
                                                    className="me-1"
                                                />
                                                Re-upload
                                            </label>
                                        ) : !certificateUrl || certificateUrl === "null" ? (
                                            <label
                                                htmlFor="certificateUpload"
                                                className="btn upload-btn mt-1 font-10 semibold-text"
                                                style={{ cursor: isSubmitting ? "not-allowed" : "pointer" }}
                                            >
                                                <img
                                                    src="/Images/upload-icon.svg"
                                                    alt="upload-icon"
                                                    className="me-1"
                                                />
                                                Upload
                                            </label>
                                        ) : (
                                            <div className="d-flex align-items-center bg-white p-2 border border-light-subtle rounded-3 shadow-sm">
                                                <img
                                                    src="/Images/pdf-icon.svg"
                                                    alt="file-icon"
                                                    className="me-1"
                                                />
                                                <span className="font-10 semibold-text text-dark">
                                                    {uploadedCertificateUrl.split('/').pop()}
                                                </span>
                                            </div>
                                        )}
                                        {validationError && (
                                            <p className="text-danger font-12">{validationError}</p>
                                        )}
                                    </>
                                )}
                            </div>
                            {/* Comments */}
                            <div className="col-md-12 mb-3">
                                <label className="font-12 mb-1 regular-text grey-text d-block">
                                    Comments
                                </label>
                                <p className="font-12 semibold-text grey-text">
                                    {certificateDetails.comments.String === "" ? "No comments yet." : certificateDetails.comments.String}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Footer with action buttons - Only show in approval mode or if file is selected for re-upload */}
                    {!isViewOnly || certificateFile ? (
                        <div className="modal-footer border-0">
                            <div className="col-md-12 d-flex justify-content-end">
                                <button
                                    type="button"
                                    className="btn hive-btn-secondary me-3"
                                    disabled={isSubmitting}
                                    onClick={handleClose}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn hive-btn-primary"
                                    onClick={handleSubmit}
                                    disabled={isSubmitting || (!certificateFile && !certificateUrl)}
                                >
                                    {isSubmitting ? "Submitting..." : "Submit"}
                                </button>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};